import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import BoxCard from "../components/BoxCard";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { X, Package } from "lucide-react";
import { useLang } from "../contexts/LangContext";

const CATEGORY_LABEL = {
  recipe: "Per Ricetta",
  experience: "Esperienza",
  regional: "Regionale",
  seasonal: "Stagionale",
  gift: "Regalo",
};

const SEASON_LABEL = {
  primavera: "Primavera",
  estate: "Estate",
  autunno: "Autunno",
  inverno: "Inverno",
};

export default function BoxShop() {
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const [boxes, setBoxes] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [region, setRegion] = useState(params.get("marchio") || "");
  const [category, setCategory] = useState(params.get("tipologia") || "");
  const [season, setSeason] = useState(params.get("anno") || "");
  const [minPrice, setMinPrice] = useState(params.get("min") || "");
  const [maxPrice, setMaxPrice] = useState(params.get("max") || "");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/boxes?limit=200").then((r) => setBoxes(r.data || []));
    api.get("/featured/boxes?limit=4").then((r) => setFeatured(r.data || [])).catch(() => {});
  }, []);

  const regions = useMemo(() => Array.from(new Set(boxes.map((b) => b.region).filter(Boolean))).sort(), [boxes]);
  const categories = useMemo(() => Array.from(new Set(boxes.map((b) => b.category).filter(Boolean))).sort(), [boxes]);
  const seasons = useMemo(() => Array.from(new Set(boxes.map((b) => b.season).filter(Boolean))).sort(), [boxes]);

  const filtered = useMemo(() => {
    return boxes.filter((b) => {
      if (region && b.region !== region) return false;
      if (category && b.category !== category) return false;
      if (season && b.season !== season) return false;
      if (minPrice && b.price < Number(minPrice)) return false;
      if (maxPrice && b.price > Number(maxPrice)) return false;
      if (q) {
        const s = q.toLowerCase();
        if (!b.name.toLowerCase().includes(s) && !(b.description || "").toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [boxes, region, category, season, minPrice, maxPrice, q]);

  useEffect(() => {
    const next = {};
    if (region) next.marchio = region;
    if (category) next.tipologia = category;
    if (season) next.anno = season;
    if (minPrice) next.min = minPrice;
    if (maxPrice) next.max = maxPrice;
    setParams(next, { replace: true });
    // eslint-disable-next-line
  }, [region, category, season, minPrice, maxPrice]);

  const reset = () => { setRegion(""); setCategory(""); setSeason(""); setMinPrice(""); setMaxPrice(""); setQ(""); };
  const hasFilter = region || category || season || minPrice || maxPrice || q;

  return (
    <div data-testid="box-shop-page">
      {/* HERO */}
      <section className="relative h-[42vh] min-h-[320px] max-h-[480px] overflow-hidden bg-[#1a0608]">
        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/static/heroes/box_hero.png`} alt="" className="absolute inset-0 w-full h-full object-cover animate-ken-burns" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
        <div className="absolute inset-y-0 left-0 w-full md:w-3/5 bg-gradient-to-r from-black/45 to-transparent" />
        <div className="relative container-xl h-full flex flex-col justify-end pb-8 md:pb-12 text-white">
          <div className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#E2A03F] font-semibold mb-3 flex items-center gap-2 animate-fade-in-up">
            <Package className="h-3.5 w-3.5" /> {t("box.eyebrow")}
          </div>
          <h1 className="editorial-heading text-4xl sm:text-5xl md:text-6xl leading-tight md:leading-none mb-3 animate-fade-in-up animation-delay-150">{t("box.title")}</h1>
          <p className="text-white/85 text-base md:text-lg max-w-2xl animate-fade-in-up animation-delay-300">
            {t("box.subtitle")}
          </p>
        </div>
      </section>

      <div className="container-xl py-8 md:py-12">

      {/* FEATURED STRIP */}
      {boxes.length > 0 && !hasFilter && (
        <div data-testid="box-featured" className="mt-10 md:mt-12 mb-12 md:mb-16 bg-gradient-to-br from-[hsl(var(--primary))]/5 to-transparent border border-[hsl(var(--primary))]/15 rounded-sm p-5 md:p-8">
          <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--primary))] font-semibold mb-3 flex items-center gap-2">
            <Package className="h-3.5 w-3.5" /> I più ordinati · in vendita
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {(featured.length ? featured : boxes.slice(0, 4)).map((b) => (<BoxCard key={b.id} box={b} />))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div data-testid="box-filters" className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <Select value={region || "all"} onValueChange={(v) => setRegion(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-region"><SelectValue placeholder={t("vini.ph_winery")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("vini.all_wineries")}</SelectItem>
            {regions.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
          </SelectContent>
        </Select>

        <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-category"><SelectValue placeholder={t("vini.ph_type")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("vini.all_types")}</SelectItem>
            {categories.map((c) => (<SelectItem key={c} value={c}>{CATEGORY_LABEL[c] || c}</SelectItem>))}
          </SelectContent>
        </Select>

        <Select value={season || "all"} onValueChange={(v) => setSeason(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-season"><SelectValue placeholder="Stagione / Anno" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("vini.all_vintages")}</SelectItem>
            {seasons.map((s) => (<SelectItem key={s} value={s}>{SEASON_LABEL[s] || s}</SelectItem>))}
          </SelectContent>
        </Select>

        <div className="grid grid-cols-2 gap-2 sm:col-span-2 lg:col-span-2">
          <Input data-testid="filter-min-price" type="number" min={0} placeholder={t("vini.ph_min")} value={minPrice} onChange={(e) => setMinPrice(e.target.value)} />
          <Input data-testid="filter-max-price" type="number" min={0} placeholder={t("vini.ph_max")} value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Input data-testid="filter-search" placeholder={t("ui.search_placeholder")} value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" />
        {hasFilter && (
          <Button data-testid="filter-reset" variant="ghost" size="sm" onClick={reset} className="gap-1 text-muted-foreground">
            <X className="h-3 w-3" /> {t("vini.reset")}
          </Button>
        )}
        <div className="ml-auto text-sm text-muted-foreground">{filtered.length} box</div>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {filtered.map((b) => (<BoxCard key={b.id} box={b} />))}
      </div>
      {filtered.length === 0 && (
        <div data-testid="box-empty" className="mt-16 text-center text-muted-foreground">
          {t("box.empty")}
        </div>
      )}
      </div>
    </div>
  );
}
